"use client";

import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { apiFetch } from "@/lib/api/client";
import type { DashboardSummary } from "../types";

type Props = { selectedDate: string };

function ChartTooltip({ active, payload, total }: { active?: boolean; payload?: Array<{ name: string; value: number }>; total: number }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]!;
  const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="bg-white border border-border rounded-lg px-3 py-2.5 shadow-modal text-sm">
      <p className="font-600 text-foreground">{item.name}</p>
      <p className="text-muted-foreground mt-0.5">
        <span className="tabular-nums font-600 text-foreground">{item.value}</span> cases ({pct}%)
      </p>
    </div>
  );
}

export default function CaseDistributionChart({ selectedDate }: Props) {
  const [data, setData] = useState<DashboardSummary['charts']['caseDistribution'] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const queryParams = selectedDate ? `?date=${selectedDate}` : "";
        const res = await apiFetch<DashboardSummary['charts']['caseDistribution']>(`/api/v1/dashboard/charts/distribution${queryParams}`, { method: "GET", auth: true });
        if (active) setData(res);
      } catch (error) {
        console.error("Failed to load case distribution chart data:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [selectedDate]);

  const byType = data?.byIpType ?? [];
  const byStatus = data?.byStatus ?? [];
  const total = byType.reduce((acc, item) => acc + item.value, 0);

  if (loading || !data) {
    return <div className="bg-white rounded-xl border border-border shadow-card p-5 h-[460px] animate-pulse" />;
  }

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5 h-full">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-600 text-foreground">Case Portfolio</h3>
          <p className="text-[12px] text-muted-foreground mt-0.5">Distribution by IP type & status</p>
        </div>
        <span className="text-[11px] font-500 bg-muted text-muted-foreground px-2.5 py-1 rounded-full">{total} total active</span>
      </div>

      <div className="mb-4">
        <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-2">By IP Type</p>
        <div className="flex items-center gap-4">
          <div className="w-28 h-28 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={byType} cx="50%" cy="50%" innerRadius={28} outerRadius={52} paddingAngle={3} dataKey="value">
                  {byType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent" />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip total={total} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {byType.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-[13px] text-foreground font-500">{item.name}</span>
                </div>
                <span className="text-[13px] font-600 tabular-nums text-foreground">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider mb-3">By Status</p>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={byStatus} cx="50%" cy="50%" outerRadius={55} paddingAngle={2} dataKey="value">
                {byStatus.map((entry, index) => (
                  <Cell key={`status-${index}`} fill={entry.color} stroke="transparent" />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip total={byStatus.reduce((a, b) => a + b.value, 0)} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
          {byStatus.map((item) => (
            <div key={item.name} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-[11px] text-muted-foreground truncate">{item.name}</span>
              <span className="text-[11px] font-600 tabular-nums text-foreground ml-auto">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
